import { useEffect, useState } from "react";
import api from "@/lib/api";
import { eur, fmtDate, Loading, Stat, EmptyState } from "@/components/common";
import Section from "@/components/Section";
import { Inbox } from "lucide-react";
import { toast } from "sonner";

const REQ_LABELS = {
  singola_uscita: "Nuova singola uscita",
  prova: "Nuova prova",
  abbonamento: "Nuovo abbonamento",
  nuova_tomba: "Nuovo luogo",
  pulizia_profonda: "Richiesta pulizia profonda",
};

export default function AdminDashboard({ onNavigate }) {
  const [kpi, setKpi] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [reqStats, setReqStats] = useState(null);
  const go = onNavigate || (() => {});

  const load = () => {
    api.get("/admin/dashboard").then((r) => setKpi(r.data));
    api.get("/admin/notifications").then((r) => setNotifications(r.data));
    api.get("/campaigns/active").then((r) => setCampaign(r.data || null)).catch(() => {});
    api.get("/admin/cemetery-requests").then((r) => setReqStats(r.data)).catch(() => {});
  };
  useEffect(load, []);

  if (!kpi) return <Loading />;

  const markRead = async (id) => {
    await api.post(`/admin/notifications/${id}/read`);
    load();
  };

  const openNotification = (n) => {
    if (!n.ref_id) return;
    if (n.request_type === "nuova_tomba") go("persone", { focus: n.ref_id });
    else go("interventi", { focus: n.ref_id });
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      {campaign && (
        <section data-testid="admin-active-campaign">
          <div className="card-soft p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            {campaign.image && <img src={campaign.image} alt={campaign.name} className="h-16 w-24 rounded-xl object-cover" />}
            <div className="flex-1">
              <p className="eyebrow mb-1">Campagna attiva ora</p>
              <p className="font-semibold text-navy">{campaign.title}</p>
              <p className="text-sm text-body">
                {campaign.product_name} · {eur(campaign.price)} · fino al {new Date(`${campaign.end_date}T12:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long" })}
              </p>
            </div>
            <span className={`text-xs font-bold rounded-full px-3 py-1.5 self-start ${campaign.virtual ? "text-[#2f5d3e] bg-[#e8f0eb]" : "text-[#1e3a5f] bg-[#f0f7ff]"}`} data-testid="admin-campaign-kind">
              {campaign.virtual ? "Automatica (calendario ricorrenze)" : "Creata da Admin"}
            </span>
          </div>
        </section>
      )}
      {reqStats && (
        <section data-testid="admin-cemetery-requests-summary">
          <button type="button" onClick={() => onNavigate("cimiteri_req")} className="card-soft p-5 w-full min-h-[56px] text-left hover:shadow-md transition-shadow duration-200" data-testid="cemetery-req-summary-btn">
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="eyebrow">Richieste nuovi cimiteri</p>
              {reqStats.unread > 0 && (
                <span className="text-xs font-bold text-white bg-[#c0392b] rounded-full px-3 py-1" data-testid="cemetery-req-unread">
                  {reqStats.unread} {reqStats.unread === 1 ? "nuova da leggere" : "nuove da leggere"}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-body">
              <span>Nuove: <b className="text-navy">{reqStats.stats.nuove}</b></span>
              <span>Da contattare: <b className="text-navy">{reqStats.stats.da_contattare}</b></span>
              <span>In valutazione: <b className="text-navy">{reqStats.stats.in_valutazione}</b></span>
            </div>
            {reqStats.top_cemeteries?.length > 0 && (
              <p className="text-xs text-muted-soft mt-2">
                Più richiesti: {reqStats.top_cemeteries.slice(0, 3).map((t) => `${t.cemetery_name} (${t.count})`).join(" · ")}
              </p>
            )}
          </button>
        </section>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat testid="kpi-richieste" label="Nuove richieste" value={kpi.nuove_richieste} onClick={() => go("dashboard")} />
        <Stat testid="kpi-da-programmare" label="Da programmare" value={kpi.da_programmare} onClick={() => go("interventi", { status: "nuova" })} />
        <Stat testid="kpi-clienti" label="Clienti" value={kpi.clienti} onClick={() => go("persone")} />
        <Stat testid="kpi-abbonamenti" label="Abbonamenti attivi" value={kpi.abbonamenti_attivi} onClick={() => go("contabilita")} />
        <Stat testid="kpi-programmati" label="Interventi programmati" value={kpi.programmati} onClick={() => go("interventi", { status: "programmata" })} />
        <Stat testid="kpi-completati" label="Interventi completati" value={kpi.completati} hint={`Ricavi: ${eur(kpi.ricavi)}`} onClick={() => go("interventi", { status: "completata" })} />
      </div>

      <Section title="Nuove richieste" count={unreadCount} icon={Inbox} testid="admin-notifications" defaultOpen={unreadCount > 0}>
        <button data-testid="read-all-btn" onClick={async () => { await api.post("/admin/notifications/read-all"); load(); toast.success("Notifiche lette."); }} className="btn-secondary !py-2 !px-4 text-xs min-h-[44px] mb-4">
          Segna tutte come lette
        </button>
        {notifications.length === 0 ? (
          <EmptyState title="Nessuna notifica" />
        ) : (
          <div className="space-y-3">
            {notifications.map((n) => (
              <div key={n.id} data-testid={`notification-${n.id}`}
                onClick={() => openNotification(n)}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") openNotification(n); }}
                className={`card-soft p-5 cursor-pointer ${n.read ? "opacity-60" : "border-l-4 !border-l-[#4a7c59]"}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div>
                    <p className="font-semibold text-navy">{REQ_LABELS[n.request_type] || n.request_type}</p>
                    <p className="text-sm text-body mt-0.5">
                      {n.customer_name} · {n.cemetery || "—"}{n.grave ? ` · ${n.grave}` : ""}
                    </p>
                    <p className="text-xs text-muted-soft mt-0.5">
                      {n.service || "—"} · {fmtDate(n.date)} · {n.amount > 0 ? eur(n.amount) : "—"} · stato: {n.status}
                    </p>
                  </div>
                  {!n.read && (
                    <button data-testid={`read-${n.id}`} onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="text-xs font-semibold text-[#4a7c59] hover:underline min-h-[44px]">
                      Segna come letta
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
